class ReadMoreToggle extends HTMLElement {
  /** @type {HTMLElement} */
  get #content() {
    return this.querySelector('[data-read-more-content]');
  }

  /** @type {HTMLElement} */
  get #toggle() {
    return this.querySelector('[data-read-more-toggle]');
  }

  /** @type {HTMLElement} */
  get #moreLabel() {
    return this.querySelector('[data-read-more-label]');
  }

  /** @type {HTMLElement} */
  get #lessLabel() {
    return this.querySelector('[data-read-less-label]');
  }

  get #maxHeight() {
    return parseInt(getComputedStyle(this).getPropertyValue('--read-more-max-height'), 10);
  }

  get #expanded() {
    return this.hasAttribute('expanded');
  }

  #controller = new AbortController();
  #resizeObserver;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#toggle.addEventListener('click', this.#handleToggle, { signal });

    this.#resizeObserver = new ResizeObserver(() => this.#checkTruncation());
    this.#resizeObserver.observe(this.#content);

    this.#checkTruncation();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver?.disconnect();
  }

  #checkTruncation() {
    const needsTruncation = this.#content.scrollHeight > this.#maxHeight;

    if (needsTruncation) {
      this.removeAttribute('no-truncation');
    } else if (!this.#expanded) {
      this.setAttribute('no-truncation', '');
    }
  }

  #handleToggle = () => {
    if (this.#expanded) {
      this.#collapse();
    } else {
      this.#expand();
    }
  };

  #expand() {
    const content = this.#content;
    const targetHeight = content.scrollHeight;

    content.style.maxHeight = `${targetHeight}px`;
    this.setAttribute('expanded', '');
    this.#moreLabel.hidden = true;
    this.#lessLabel.hidden = false;

    content.addEventListener(
      'transitionend',
      () => {
        content.style.maxHeight = 'none';
      },
      { once: true }
    );
  }

  #collapse() {
    const content = this.#content;

    content.style.maxHeight = `${content.scrollHeight}px`;
    requestAnimationFrame(() => {
      content.style.maxHeight = '';
      this.removeAttribute('expanded');
      this.#moreLabel.hidden = false;
      this.#lessLabel.hidden = true;
    });
  }
}

if (!customElements.get('read-more-toggle')) {
  customElements.define('read-more-toggle', ReadMoreToggle);
}
